import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getFolderPath } from '../../../../utils/docOrganization';

/**
 * Folder pill + dropdown — library organization context only.
 * Shows nested folder paths.
 */
const DocFolderMenu = ({ organization, docId }) => {
  const [showFolderMenu, setShowFolderMenu] = useState(false);
  const orgReady = !!organization?.ready;
  const currentFolderId = organization?.getDocFolderId?.(docId) || null;
  const folders = organization?.folders || [];
  const currentFolder = folders.find((f) => f.id === currentFolderId) || null;

  const folderOptions = useMemo(() => {
    return folders.map((f) => {
      const path = getFolderPath(folders, f.id);
      return {
        id: f.id,
        label: path.map((p) => p.name).join(' / ') || f.name,
        depth: Math.max(0, path.length - 1),
      };
    });
  }, [folders]);

  useEffect(() => {
    if (!showFolderMenu) return undefined;
    const close = () => setShowFolderMenu(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showFolderMenu]);

  const handleToggleFolderMenu = useCallback((e) => {
    e.stopPropagation();
    setShowFolderMenu((v) => !v);
  }, []);

  const handlePickFolder = useCallback(
    (folderId) => {
      setShowFolderMenu(false);
      organization?.moveDocToFolder(docId, folderId);
    },
    [organization, docId]
  );

  return (
    <div className="doc-fullpage__folder">
      <button
        className={`doc-fullpage__folder-pill ${currentFolder ? 'has-folder' : ''}`}
        onClick={handleToggleFolderMenu}
        disabled={!orgReady}
        title={currentFolder ? `In folder "${currentFolder.name}"` : 'Add to a folder'}
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
        <span>{currentFolder ? currentFolder.name : 'No folder'}</span>
      </button>
      {showFolderMenu && (
        <div className="doc-fullpage__folder-menu" onClick={(e) => e.stopPropagation()}>
          {folderOptions.length === 0 && (
            <div className="doc-fullpage__folder-menu-hint">
              No folders yet — create one in the Docs sidebar or Atlas
            </div>
          )}
          {folderOptions.map((folder) => (
            <button
              key={folder.id}
              className="doc-fullpage__folder-menu-item"
              style={{ paddingLeft: 10 + folder.depth * 8 }}
              onClick={() =>
                handlePickFolder(currentFolderId === folder.id ? null : folder.id)
              }
            >
              <span className="doc-fullpage__folder-menu-item-grow">{folder.label}</span>
              {currentFolderId === folder.id && <span aria-hidden>✓</span>}
            </button>
          ))}
          {currentFolderId && (
            <button
              className="doc-fullpage__folder-menu-item"
              onClick={() => handlePickFolder(null)}
            >
              Remove from folder
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DocFolderMenu;
